import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { SubmitHandler, useForm, UseFormTrigger } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import { NonProfitOrgType } from '@/api/models/nonProfitOrgModels';
import { useAuth } from '@/contexts/authContext';
import { SalaryData, salaryResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

import AddAnotherButton from '../buttons/AddAnotherButton';
import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';

interface SalaryFormrops {
  initialData?: SalaryData;
  showDoneButton: boolean;
  showBackButton?: boolean;
  onAdd?: (data: SalaryData) => void;
  onDone?: (data: SalaryData, trigger: UseFormTrigger<SalaryData>) => void;
  manualValidation?: boolean;
  doneLabel?: string;
  isLoading?: boolean;
  type?: NonProfitOrgType;
}

const SalaryForm = ({
  initialData,
  showDoneButton,
  showBackButton = false,
  onDone,
  manualValidation = false,
  onAdd,
  doneLabel = i18n.t('mix.done'),
  isLoading = false,
  type
}: SalaryFormrops) => {
  const { state } = useAuth();

  const npoType = type ?? state.signUpData?.loginEmail?.type;

  const isIndividual = state.authReady && state.user?.user.role === 'INDIVIDUAL';

  const {
    control,
    getValues,
    setFocus,
    reset,
    trigger,
    formState: { isValid, submitCount, errors }
  } = useForm<SalaryData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: salaryResolver as any,
    context: { isIndividual }
  });

  const onSubmit: SubmitHandler<SalaryData> = async (data) => {
    if (onDone) {
      onDone(data, trigger);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="position"
          control={control}
          label={i18n.t('sign_in.nonProfit.salaries.job_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.salaries.job_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.salaries.job_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('annualSalary')}
        />

        <TextInputField
          name="fullName"
          control={control}
          label={i18n.t('sign_in.nonProfit.salaries.name_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.salaries.name_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.salaries.name_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('position')}
        />

        <TextInputField
          name="annualSalary"
          control={control}
          label={i18n.t('sign_in.nonProfit.salaries.salary_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.salaries.salary_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.salaries.salary_placeholder')}
          type="numeric"
          currency
          returnKeyType="done"
          onSubmitEditing={() => setFocus('allowance')}
        />

        {npoType === 'CHURCH' && (
          <TextInputField
            name="allowance"
            control={control}
            label={i18n.t('sign_in.nonProfit.salaries.allowance_label')}
            // infoSubLabel={i18n.t('sign_in.nonProfit.salaries.allowance_sublabel')}
            placeholder={i18n.t('sign_in.nonProfit.salaries.allowance_placeholder')}
            type="numeric"
            currency
            returnKeyType="done"
          />
        )}

        {onAdd && (
          <AddAnotherButton
            onPress={() => {
              trigger().then((valid) => {
                if (valid) onAdd(getValues());
              });
            }}
            label={i18n.t('sign_in.nonProfit.salaries.submit_and_add')}
          />
        )}
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      {showDoneButton && (
        <View style={styles.nextBackContainer}>
          {showBackButton && router.canGoBack() && (
            <BrandedButton style={styles.flexOne} disabled={isLoading} onPress={router.back}>
              {capitalize(i18n.t('mix.back'))}
            </BrandedButton>
          )}
          <BrandedButton
            style={styles.flexOne}
            onPress={() => {
              if (manualValidation) {
                onSubmit(getValues());
              } else {
                trigger().then((valid) => {
                  if (valid) onSubmit(getValues());
                });
              }
            }}
            disabled={(!isValid && submitCount > 0) || isLoading}
            loading={isLoading}>
            {doneLabel}
          </BrandedButton>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    flex: 1,
    gap: 15
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default SalaryForm;
